import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    Text,
} from 'grommet';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { API, Utils } from '@enrich/shared-lib';
import QueryString from 'qs';

const ACTIVE_CANDIDACY_STATUSES = [
    'interested',
    'missed_apply_deadline',
    'applied',
    'application_rejected',
    'assessment',
    'interview',
    'second_interview',
    'offer_received',
    'offer_accepted',
    'offer_rejected',
];

export const CompanyCandidates = () => {
    const company = useLoaderData();
    const navigate = useNavigate();
    const companySlug = company.attributes.slug;
    const stepOptions = [25, 50, 100];
    const [candidates, setCandidates] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);
    const [sort, setSort] = useState();

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadCandidates({
                page,
                pageSize,
                companySlug,
                sort,
            });

            if (isActive) {
                setPage(response.meta?.pagination?.page || 1);
                setCandidates(response.data || []);
                setTotalItems(response.meta?.pagination?.total || 0);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, companySlug, sort]);

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => (
                <Anchor
                    key={contact.id}
                    href={`/contacts/${contact.id}`}
                    label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            header: 'Job',
            property: 'attributes.candidacies.data',
            render: (contact) => {
                const candidacies =
                    contact.attributes.candidacies?.data || [];
                if (candidacies.length === 0) return <Text>N/A</Text>;
                return (
                    <Box gap="xsmall">
                        {candidacies.map((candidacy) => (
                            <Anchor
                                key={candidacy.id}
                                href={`/jobs/${candidacy.attributes.job?.data?.id}`}
                                label={
                                    candidacy.attributes.job?.data?.attributes
                                        ?.title || 'N/A'
                                }
                            />
                        ))}
                    </Box>
                );
            },
        },
        {
            header: 'Status',
            property: 'attributes.candidacies',
            render: (contact) => {
                const candidacies =
                    contact.attributes.candidacies?.data || [];
                if (candidacies.length === 0) return <Text>N/A</Text>;
                return (
                    <Box gap="xsmall">
                        {candidacies.map((candidacy) => (
                            <Text key={candidacy.id}>
                                {Utils.convertEnumStringToLabel(
                                    candidacy.attributes.status,
                                )}
                            </Text>
                        ))}
                    </Box>
                );
            },
        },
        {
            header: 'Graduation',
            property: 'attributes.graduationYear',
            render: (contact) => {
                const semester = contact.attributes.graduationSemester;
                const formattedSemester = semester
                    ? semester[0].toUpperCase() + semester.slice(1)
                    : '';
                return `${formattedSemester} ${contact.attributes.graduationYear || ''}`;
            },
            sortable: true,
        },
        {
            header: 'School',
            property: 'attributes.school.data.attributes.name',
            render: (contact) =>
                contact.attributes.school?.data?.attributes?.name || 'N/A',
            sortable: true,
        },
        {
            header: 'Hometown',
            property: 'attributes.hometown',
            sortable: true,
        },
        {
            header: 'Majors',
            property: 'attributes.majors',
            render: (contact) => {
                const majors = contact?.attributes.majors?.data?.map(
                    (major) => major.attributes.name,
                );
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
    ];

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
        }
        setPage(page);
    };

    const handleSort = ({ property, direction }) => {
        setSort({ property, direction });
        setPage(1);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Box>
                        <Heading level="2">
                            {company.attributes.name}
                        </Heading>
                        <Heading level="3" margin={{ top: 'none' }}>
                            Active Candidates
                        </Heading>
                    </Box>
                    <Box>
                        <Anchor
                            href={`/companies/${company.id}`}
                            label="← Back to Company"
                        />
                    </Box>
                </Box>
                <Box>
                    {candidates.length === 0 ? (
                        <Box pad="medium" align="center">
                            <Text>No active candidates found</Text>
                        </Box>
                    ) : (
                        <DataTable
                            primaryKey="id"
                            sort={sort}
                            onSort={handleSort}
                            border="bottom"
                            data={candidates}
                            columns={dataColumns}
                        />
                    )}
                </Box>
                <Box align="center" justify="center" pad="small">
                    <Pagination
                        numberItems={totalItems}
                        onChange={({ page, startIndex, endIndex }) => {
                            handlePageChange({
                                page,
                                startIndex,
                                endIndex,
                            });
                        }}
                        step={pageSize}
                        stepOptions={stepOptions}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

export async function loader({ params }) {
    const query = QueryString.stringify({
        fields: ['id', 'name', 'slug'],
    });

    try {
        const response = await API.get(
            `/api/companies/${params.companyId}?${query}`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}

const SORT_FIELD_MAP = {
    'attributes.lastName': 'lastName',
    'attributes.graduationYear': 'graduationYear',
    'attributes.school.data.attributes.name': 'school.name',
    'attributes.hometown': 'hometown',
};

const loadCandidates = async ({
    page,
    pageSize,
    companySlug,
    sort,
} = {}) => {
    const sortField = sort && SORT_FIELD_MAP[sort.property];
    const query = QueryString.stringify(
        {
            filters: {
                candidacies: {
                    status: { $in: ACTIVE_CANDIDACY_STATUSES },
                    job: {
                        status: 'open',
                        company: { slug: { $eqi: companySlug } },
                    },
                },
            },
            populate: {
                school: { fields: ['name'] },
                majors: { fields: ['name'] },
                candidacies: {
                    populate: { job: { fields: ['id', 'title'] } },
                    filters: {
                        status: { $in: ACTIVE_CANDIDACY_STATUSES },
                        job: {
                            status: 'open',
                            company: { slug: { $eqi: companySlug } },
                        },
                    },
                },
            },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: sortField
                ? [`${sortField}:${sort.direction}`]
                : ['lastName:asc'],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0 } } };
};
